import * as React from "react";
import { mergeClassNames } from "./class-names";

const headingElements = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6",
} as const;

const headingSizeClassNames = {
  xxlarge: "text-heading-xxlarge",
  xlarge: "text-heading-xlarge",
  large: "text-heading-large",
  medium: "text-heading-medium",
  small: "text-heading-small",
  xsmall: "text-heading-xsmall",
  xxsmall: "text-heading-xxsmall",
} as const;

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  readonly level: keyof typeof headingElements;
  readonly size?: keyof typeof headingSizeClassNames;
}

export function Heading({
  level,
  size = level === 1 ? "xxlarge" : level === 2 ? "large" : "medium",
  className,
  ...props
}: HeadingProps) {
  const Element = headingElements[level];
  return (
    <Element
      data-slot="heading"
      className={mergeClassNames(
        "min-w-0 font-sans text-primary [overflow-wrap:anywhere]",
        headingSizeClassNames[size],
        className,
      )}
      {...props}
    />
  );
}

const textSizeClassNames = {
  large: "text-body-large",
  default: "text-body",
  small: "text-body-small",
} as const;

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  readonly as?: "p" | "span" | "div";
  readonly size?: keyof typeof textSizeClassNames;
}

export function Text({
  as: Element = "p",
  size = "default",
  className,
  ...props
}: TextProps) {
  return (
    <Element
      data-slot="text"
      className={mergeClassNames(
        "min-w-0 [overflow-wrap:anywhere]",
        textSizeClassNames[size],
        className,
      )}
      {...props}
    />
  );
}

export interface MetricTextProps extends React.HTMLAttributes<HTMLElement> {
  readonly as?: "p" | "span" | "div";
  readonly size?: "large" | "medium";
}

export function MetricText({
  as: Element = "span",
  size = "medium",
  className,
  ...props
}: MetricTextProps) {
  return (
    <Element
      data-slot="metric-text"
      className={mergeClassNames(
        "font-mono tabular-nums text-primary",
        size === "large" ? "text-metric-large" : "text-metric-medium",
        className,
      )}
      {...props}
    />
  );
}

export function VisuallyHidden({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      data-slot="visually-hidden"
      className={mergeClassNames(
        "absolute size-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)] [clip-path:inset(50%)]",
        className,
      )}
      {...props}
    />
  );
}

export function SkipLink({
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      data-slot="skip-link"
      className={mergeClassNames(
        "fixed start-4 top-4 z-[var(--z-index-toast)] -translate-y-24 rounded-md bg-action-primary px-4 py-3 text-body font-semibold text-action-primary-ink shadow-elevation-floating transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-surface motion-reduce:transition-none",
        className,
      )}
      {...props}
    />
  );
}
